import { Component, EventEmitter, Input, Output, OnInit, OnDestroy, OnChanges, SimpleChanges, inject } from '@angular/core';
import { ModelsUser } from "../../../app/api/model/models";
import { DialogModule } from "primeng/dialog";
import { ButtonModule } from "primeng/button";
import { FormsModule } from "@angular/forms";
import { InputTextModule } from "primeng/inputtext";
import { Ripple } from 'primeng/ripple';
import { TranslocoModule } from '@jsverse/transloco';
import { MultiSelectModule } from 'primeng/multiselect';
import { SelectModule } from 'primeng/select';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { InputGroup } from 'primeng/inputgroup';
import { InputGroupAddon } from 'primeng/inputgroupaddon';
import { ModelsCompanyPosition } from '../../api/model/models';
import { AuthService } from '../../../services/AuthService';

@Component({
  selector: 'app-hr-employee-form',
  template: `
    <p-dialog [visible]="visible" (visibleChange)="onVisibleChange($event)" [position]="dialogPosition" [breakpoints]="{ '640px': '100vw' }" [style]="{ width: '450px', maxWidth: '100vw' }" [header]="(employee.id ? 'Employee Details' : 'Add Employee') | transloco" [modal]="true"
              styleClass="p-fluid">
      <ng-template pTemplate="content">
        <div class="flex flex-col gap-4 mt-2">
          <div class="grid grid-cols-2 gap-4">
            <div class="flex flex-col gap-2">
              <label for="first_name" class="font-semibold">{{ 'First Name' | transloco }}</label>
              <input type="text" pInputText id="first_name" [(ngModel)]="employee.first_name" required autofocus/>
            </div>
            <div class="flex flex-col gap-2">
              <label for="last_name" class="font-semibold">{{ 'Last Name' | transloco }}</label>
              <input type="text" pInputText id="last_name" [(ngModel)]="employee.last_name" required/>
            </div>
          </div>
          <div class="flex flex-col gap-2">
            <label for="phone" class="font-semibold">{{ 'Phone' | transloco }}</label>
            @if (isIsraelTimezone) {
              <p-inputgroup dir="ltr">
                <p-inputgroup-addon>+972</p-inputgroup-addon>
                <input 
                  type="text" 
                  pInputText 
                  id="phone" 
                  [(ngModel)]="phoneSubscriber" 
                  (keyup)="onPhoneKeyup()"
                  (input)="onPhoneKeyup()"
                  placeholder="501234567"
                  [invalid]="showPhoneError"
                  required/>
              </p-inputgroup>
            } @else {
              <input 
                type="text" 
                pInputText 
                id="phone" 
                [(ngModel)]="employee.phone" 
                (keyup)="onPhoneKeyup()"
                (input)="onPhoneKeyup()"
                placeholder="155512345678"
                [invalid]="showPhoneError"
                required/>
            }
            @if (showPhoneError) {
              <small class="text-red-500 text-xs">
                {{ phoneErrorMessage | transloco }}
              </small>
            }
          </div>
          <div class="flex flex-col gap-2">
            <label for="positions" class="font-semibold">{{ 'Position' | transloco }}</label>
            <p-multiSelect 
              id="positions" 
              [options]="positions" 
              [(ngModel)]="employee.positions" 
              (onChange)="onPositionsChange()"
              [disabled]="isOwner"
              optionLabel="name" 
              dataKey="id"
              [placeholder]="'Select Positions' | transloco"
              display="chip"
              appendTo="body"
            />
          </div>
          <!-- <div class="flex flex-col gap-2">
            <label for="role" class="font-semibold">{{ 'Role' | transloco }}</label>
            <p-select 
              id="role" 
              [options]="roles" 
              [(ngModel)]="employee.role" 
              [disabled]="!canEditRole"
              optionLabel="label"
              optionValue="value"
              appendTo="body">
              <ng-template pTemplate="selectedItem" let-selectedOption>
                {{ selectedOption.label | transloco }}
              </ng-template>
              <ng-template pTemplate="item" let-option>
                {{ option.label | transloco }}
              </ng-template>
            </p-select>
          </div> -->
          <div class="flex flex-row items-center justify-between mt-2">
            <label for="wa_opt_in" class="font-semibold">{{ 'Confirm privacy policy and receive messages on WhatsApp' | transloco }}</label>
            <p-toggleSwitch 
              id="wa_opt_in" 
              [(ngModel)]="employee.wa_opt_in" 
            />
          </div>
        </div>
      </ng-template>

      <ng-template pTemplate="footer">
        <button pButton pRipple [label]="'Cancel' | transloco" icon="pi pi-times" class="p-button-text" (click)="hideDialog()"></button>
        <button pButton pRipple [label]="'Save' | transloco" icon="pi pi-check" class="p-button-text" (click)="saveEmployee()"
                [disabled]="isSaveDisabled"></button>
      </ng-template>
    </p-dialog>
  `,
  standalone: true,
  imports: [
    DialogModule,
    ButtonModule,
    FormsModule,
    InputTextModule,
    Ripple,
    TranslocoModule,
    MultiSelectModule,
    SelectModule,
    ToggleSwitchModule,
    InputGroup,
    InputGroupAddon
  ]
})
export class HrEmployeesForm implements OnInit, OnDestroy, OnChanges {
  @Input() visible = false;
  @Input() employee: ModelsUser = {} as ModelsUser;
  @Input() positions: ModelsCompanyPosition[] = [];
  @Input() canEditRole = false;
  @Output() onSave = new EventEmitter<ModelsUser>();
  @Output() onCancel = new EventEmitter<void>();

  private auth = inject(AuthService);

  dialogPosition: 'center' | 'top' = 'center';

  phoneSubscriber = '';
  phoneDirty = false;
  isSubmitted = false;

  roles = [
    { label: 'Employee', value: 'employee' },
    { label: 'Manager', value: 'manager' },
    { label: 'Owner', value: 'owner' }
  ];

  get isIsraelTimezone(): boolean {
    return this.auth.currentUser()?.company?.timezone === 'Asia/Jerusalem';
  }

  get isOwner(): boolean {
    const roles = (this.employee?.role || '').split(',').map(r => r.trim().toLowerCase());
    return roles.includes('owner');
  }

  get enteredPhone(): string {
    return (this.isIsraelTimezone ? this.phoneSubscriber : this.employee?.phone) || '';
  }

  get isPhoneValid(): boolean {
    const val = this.enteredPhone.trim();
    if (this.isIsraelTimezone) {
      return /^5\d{8}$/.test(val);
    }
    return /^[1-9]\d{7,14}$/.test(val);
  }

  get showPhoneError(): boolean {
    const val = this.enteredPhone.trim();
    if (!val) {
      return this.isSubmitted;
    }
    return (this.phoneDirty || this.isSubmitted) && !this.isPhoneValid;
  }

  get phoneErrorMessage(): string {
    const val = this.enteredPhone.trim();

    if (this.isIsraelTimezone) {
      if (val.startsWith('+972') || val.startsWith('972')) {
        return 'Prefix +972 is already included';
      }
      if (val.startsWith('0')) {
        return 'Phone number should not start with 0';
      }
      if (/\D/.test(val)) {
        return 'Phone number must contain digits only';
      }
      if (val.length < 9) {
        return 'Phone number is too short';
      }
      if (val.length > 9) {
        return 'Phone number is too long';
      }
      if (!val.startsWith('5')) {
        return 'Mobile number must start with 5';
      }
      return 'Invalid Israeli phone format';
    } else {
      if (val.startsWith('+')) {
        return 'Phone number should be without +';
      }
      if (val.startsWith('0')) {
        return 'Please include country code without +';
      }
      if (/\D/.test(val)) {
        return 'Phone number must contain digits only';
      }
      if (val.length < 8) {
        return 'Phone number is too short';
      }
      if (val.length > 15) {
        return 'Phone number is too long';
      }
      return 'Invalid international phone format';
    }
  }

  get isSaveDisabled(): boolean {
    if (!this.employee?.first_name?.trim() || !this.employee?.last_name?.trim()) {
      return true;
    }
    return !this.isPhoneValid;
  }

  private syncPhoneState() {
    if (!this.employee) return;
    this.phoneDirty = false;
    this.isSubmitted = false;

    if (this.isIsraelTimezone) {
      let raw = (this.employee.phone || '').trim();
      if (raw.startsWith('+972')) {
        raw = raw.slice(4);
      } else if (raw.startsWith('972') && raw.length > 8) {
        raw = raw.slice(3);
      }
      if (raw.startsWith('0')) {
        raw = raw.replace(/^0+/, '');
      }
      this.phoneSubscriber = raw;
    } else {
      let raw = (this.employee.phone || '').trim();
      if (raw.startsWith('+')) {
        raw = raw.slice(1);
      }
      this.employee.phone = raw;
    }
  }

  onPhoneKeyup() {
    this.phoneDirty = true;
  }

  onPositionsChange() {
    if (!this.employee || this.isOwner) {
      return;
    }

    const selectedPositions = this.employee.positions || [];
    const roles: string[] = [];

    for (const sp of selectedPositions) {
      const fullPos = this.positions.find(p => p.id === sp.id) || sp;
      const role = fullPos.position?.role;
      if (role) {
        role.split(',').forEach(r => {
          const cleanRole = r.trim().toLowerCase();
          if (cleanRole && !roles.includes(cleanRole)) {
            roles.push(cleanRole);
          }
        });
      }
    }

    if (roles.includes('owner')) {
      this.employee.role = this.canEditRole ? 'owner' : 'manager';
    } else if (roles.includes('manager')) {
      this.employee.role = 'manager';
    } else {
      this.employee.role = 'employee';
    }
  }

  private onViewportResize = () => {
    if (typeof window !== 'undefined' && window.visualViewport) {
      const isKeyboardOpen = window.visualViewport.height < window.innerHeight * 0.85;
      this.dialogPosition = isKeyboardOpen ? 'top' : 'center';
    }
  };

  ngOnInit() {
    if (this.visible) {
      this.syncPhoneState();
    }
    if (typeof window !== 'undefined' && window.visualViewport) {
      window.visualViewport.addEventListener('resize', this.onViewportResize);
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['visible']?.currentValue === true || changes['employee']) {
      this.syncPhoneState();
    }
  }

  ngOnDestroy() {
    if (typeof window !== 'undefined' && window.visualViewport) {
      window.visualViewport.removeEventListener('resize', this.onViewportResize);
    }
  }

  onVisibleChange(isVisible: boolean) {
    if (!isVisible) {
      this.hideDialog();
    }
  }

  hideDialog() {
    this.dialogPosition = 'center';
    this.phoneDirty = false;
    this.isSubmitted = false;
    this.onCancel.emit();
  }

  saveEmployee() {
    this.isSubmitted = true;
    if (this.isSaveDisabled) {
      return;
    }
    if (this.isIsraelTimezone) {
      this.employee.phone = '972' + this.phoneSubscriber.trim();
    } else {
      this.employee.phone = this.employee.phone.trim();
    }
    this.onSave.emit(this.employee);
  }
}
